import React, { useContext, useState, useMemo, useRef, useEffect } from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import { ThemeContext } from 'styled-components';
import { useQuery, useQueryClient } from 'react-query';
import { getBillRules, deleteBillRule, reorderBillRules } from '../../../services/bill/BillRules';
import { CsrfContext } from '../../../providers/CsrfProvider';
import CreateRuleForm from './CreateRuleForm';
import EditRuleForm from './EditRuleForm';
import EditPercentRulePercentageForm from './EditPercentRulePercentageForm';
import BillRuleHistoryDropdown from './BillRuleHistoryDropdown';
import TestSplitsModal from './TestSplitsModal';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import TableButton from './TableButton';
import WithActions from '../../common/components/Wrappers/WithActions';
import TableWithActions from '../../common/components/Wrappers/TableWithActions';
import { DotsSixVertical, PencilSimple, ArrowsDownUp } from '@phosphor-icons/react';
import FormatNumber from '../../../utils/formatters/FormatNumber';

const RulesList = ({ templateId, disabled = false, enabled = true, hydrating = false, onHydrated, expandedView = false, headerAction }) => {
    const theme = useContext(ThemeContext);
    const { csrfToken } = useContext(CsrfContext);
    const queryClient = useQueryClient();
    const [isCreatingRule, setIsCreatingRule] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [editingPercentRulePercentage, setEditingPercentRulePercentage] = useState(null);
    const [deletingRuleId, setDeletingRuleId] = useState(null);
    const [draggedRuleId, setDraggedRuleId] = useState(null);
    const [dragOverRuleId, setDragOverRuleId] = useState(null);
    const [dragOverPosition, setDragOverPosition] = useState(null); // 'before' or 'after'
    const [optimisticRules, setOptimisticRules] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRuleId, setMenuRuleId] = useState(null);
    const [testModalOpen, setTestModalOpen] = useState(false);

    // Fetch rules for this bill (only when enabled so parent can gate fetch)
    const { data: rules, isLoading, isError } = useQuery(
        ['billRules', String(templateId)],
        () => getBillRules(String(templateId), csrfToken),
        {
            enabled: !!templateId && !!csrfToken && templateId !== 'none' && (enabled !== false),
            refetchOnMount: true,
            refetchOnWindowFocus: false,
        }
    );

    const hydratedRef = useRef(false);
    useEffect(() => {
        hydratedRef.current = false;
    }, [templateId]);
    // Only report hydrated when the query was enabled and has settled (data or error), not when disabled or still loading
    useEffect(() => {
        if (onHydrated && enabled && !isLoading && (rules !== undefined || isError) && !hydratedRef.current) {
            hydratedRef.current = true;
            onHydrated();
        }
    }, [enabled, isLoading, isError, rules, onHydrated]);

    // Use optimistic rules if available for immediate UI feedback
    const rulesToDisplay = optimisticRules || rules;

    // Separate default rule and non-default rules, then combine with default at the top
    const { nonDefaultRules, defaultRule } = useMemo(() => {
        if (!rulesToDisplay) return { nonDefaultRules: [], defaultRule: null };

        const nonDefault = rulesToDisplay
            .filter(rule => !rule.is_default)
            .sort((a, b) => (a.position || 0) - (b.position || 0));

        const defaultR = rulesToDisplay.find(rule => rule.is_default) || null;

        return { nonDefaultRules: nonDefault, defaultRule: defaultR };
    }, [rulesToDisplay]);

    // Combine rules for display: default first, then non-default rules
    const allRulesForDisplay = useMemo(() => {
        const result = [];
        if (defaultRule) {
            result.push(defaultRule);
        }
        result.push(...nonDefaultRules);
        return result;
    }, [nonDefaultRules, defaultRule]);

    const handleDeleteRule = async (ruleId) => {
        setDeletingRuleId(ruleId);
        handleMenuClose();
        try {
            await deleteBillRule(String(templateId), ruleId, csrfToken);
            queryClient.invalidateQueries(['billRules', String(templateId)]);
            queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);
        } catch (err) {
            console.error('Error deleting rule:', err);
            alert(err.response?.data?.error || err.message || 'Failed to delete rule');
        } finally {
            setDeletingRuleId(null);
        }
    };

    const handleMenuClick = (event, ruleId) => {
        event.preventDefault();
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setMenuRuleId(ruleId);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRuleId(null);
    };

    const handleEditFromMenu = (rule) => {
        handleMenuClose();
        setEditingRule(rule);
    };

    const handleDragStart = (e, ruleId) => {
        setDraggedRuleId(ruleId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', ruleId);
        e.stopPropagation();
    };

    const handleDragOver = (e, ruleId) => {
        e.preventDefault();
        e.stopPropagation();

        if (!draggedRuleId || draggedRuleId === ruleId) {
            setDragOverRuleId(null);
            setDragOverPosition(null);
            return;
        }

        // Check if the target rule is the default rule - if so, don't allow dropping
        const currentRules = rules || [];
        const targetRule = currentRules.find(r => r.id === ruleId);
        if (targetRule?.is_default) {
            setDragOverRuleId(null);
            setDragOverPosition(null);
            return;
        }

        // Get the row element and calculate if we're past the halfway point
        const rowElement = e.currentTarget;
        const rect = rowElement.getBoundingClientRect();
        const mouseY = e.clientY;
        const rowTop = rect.top;
        const rowHeight = rect.height;
        const rowMiddle = rowTop + (rowHeight / 2);

        // Determine position based on which half of the row we're in
        // Use the middle as the dividing line
        if (mouseY < rowMiddle) {
            // Top half - insert before (above) this row
            setDragOverRuleId(ruleId);
            setDragOverPosition('before');
        } else if (mouseY > rowMiddle) {
            // Bottom half - insert after (below) this row
            setDragOverRuleId(ruleId);
            setDragOverPosition('after');
        }
        // If exactly at middle, don't change state (keep previous)
    };

    const handleDragLeave = () => {
        setDragOverRuleId(null);
        setDragOverPosition(null);
    };

    const handleDrop = async (e, targetRuleId) => {
        e.preventDefault();
        e.stopPropagation();

        if (!draggedRuleId || draggedRuleId === targetRuleId || !dragOverRuleId || !dragOverPosition) {
            setDraggedRuleId(null);
            setDragOverRuleId(null);
            setDragOverPosition(null);
            setOptimisticRules(null);
            return;
        }

        // Get current rules (use original rules, not optimistic)
        const currentRules = rules || [];
        const allNonDefaultRules = currentRules.filter(rule => !rule.is_default);

        // Prevent moving if either rule is the default rule
        const draggedRule = currentRules.find(r => r.id === draggedRuleId);
        const targetRule = currentRules.find(r => r.id === targetRuleId);
        if (draggedRule?.is_default || targetRule?.is_default) {
            setDraggedRuleId(null);
            setDragOverRuleId(null);
            setDragOverPosition(null);
            setOptimisticRules(null);
            return;
        }

        const oldIndex = allNonDefaultRules.findIndex(r => r.id === draggedRuleId);
        const targetIndex = allNonDefaultRules.findIndex(r => r.id === targetRuleId);

        if (oldIndex === -1 || targetIndex === -1 || oldIndex === targetIndex) {
            setDraggedRuleId(null);
            setDragOverRuleId(null);
            setDragOverPosition(null);
            setOptimisticRules(null);
            return;
        }

        // Calculate drop position based on dragOverPosition
        let newIndex;
        if (dragOverPosition === 'after') {
            // Insert after the target row (below)
            newIndex = targetIndex + 1;
        } else {
            // Insert before the target row (above)
            newIndex = targetIndex;
        }

        // Adjust if dragging from before the target
        if (oldIndex < newIndex) {
            newIndex = newIndex - 1;
        }

        // Clamp to valid range
        newIndex = Math.max(0, Math.min(newIndex, allNonDefaultRules.length - 1));

        // Reorder rules
        const reordered = [...allNonDefaultRules];
        const [moved] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, moved);

        // Update positions
        const updatedRules = reordered.map((rule, index) => ({
            ...rule,
            position: index + 1,
        }));

        // Combine with default rule
        const defaultRule = currentRules.find(r => r.is_default);
        const allRules = defaultRule ? [...updatedRules, defaultRule] : updatedRules;

        // Optimistically update UI
        setOptimisticRules(allRules);

        // Get rule IDs in new order
        const ruleIds = reordered.map(r => r.id);

        try {
            await reorderBillRules(String(templateId), ruleIds, csrfToken);
            await queryClient.invalidateQueries(['billRules', String(templateId)]);
            queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);
            setTimeout(() => {
                setOptimisticRules(null);
            }, 100);
        } catch (err) {
            console.error('Error reordering rules:', err);
            setOptimisticRules(null);
            alert(err.response?.data?.error || err.message || 'Failed to reorder rules');
        } finally {
            setDraggedRuleId(null);
            setDragOverRuleId(null);
            setDragOverPosition(null);
        }
    };

    const handleDragEnd = () => {
        setDraggedRuleId(null);
        setDragOverRuleId(null);
        setDragOverPosition(null);
    };

    return (
        <Box
            sx={{
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                borderRadius: '8px',
                mb: '40px', // Standardized bottom spacing to match top spacing
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
            }}
        >

            {/* While dehydrated: show table with one empty row and loading animation (no Test/add UI) */}
            {hydrating ? (
                <TableContainer
                    component={Paper}
                    sx={{
                        mb: 0,
                        borderRadius: expandedView ? 0 : 2,
                        backgroundColor: expandedView ? 'transparent' : (theme.colors.tableBackground || theme.colors.background),
                        boxShadow: expandedView ? 'none' : (theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)'),
                        overflow: 'visible',
                        '& .MuiTable-root': {
                            '& .MuiTableBody-root': {
                                '& .MuiTableRow-root:last-child': {
                                    '& .MuiTableCell-root': {
                                        borderBottom: 'none',
                                    },
                                },
                            },
                        },
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, verticalAlign: 'middle' }}>
                                    {headerAction ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                            {headerAction}
                                        </Box>
                                    ) : null}
                                </TableCell>
                                <TableCell sx={{ color: theme.colors.brandSecondary, fontWeight: 700, fontSize: '1.75rem', pl: 0, py: 2 }}>Rules</TableCell>
                                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                    <CircularProgress sx={{ color: theme.colors?.primary || theme.colors?.brandSecondary }} />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <>
            {/* Loading state (when not using hydrating placeholder) */}
            {isLoading && (
                <Box
                    sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: '4px',
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.colors.text,
                            opacity: 0.7,
                        }}
                    >
                        Loading rules...
                    </Typography>
                </Box>
            )}

            {/* Error state */}
            {isError && (
                <Box
                    sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: '4px',
                        backgroundColor: theme.colors.background || 'transparent',
                        border: `1px solid ${theme.colors.error || '#f44336'}`,
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.colors.error || '#f44336',
                        }}
                    >
                        Error loading rules
                    </Typography>
                </Box>
            )}

            {/* Rules table */}
            {!isLoading && !isError && (
                <>
                <TableWithActions
                    showLeftAction="buffer"
                    showRightAction={true}
                    actionColumnWidth="56px"
                    forceSpacing={true}
                    renderLeftAction={(rule) => {
                        const isDefault = rule.is_default;
                        const isDraggable = !disabled && !isDefault;
                        if (isDraggable) {
                            return (
                                <Box
                                    draggable={isDraggable}
                                    onDragStart={(e) => handleDragStart(e, rule.id)}
                                    sx={{
                                        cursor: isDraggable ? 'grab' : 'default',
                                        '&:active': {
                                            cursor: isDraggable ? 'grabbing' : 'default',
                                        },
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >
                                    <DotsSixVertical size={20} color={theme.colors.text} weight="bold" style={{ opacity: 0.5 }} />
                                </Box>
                            );
                        }
                        return null;
                    }}
                    renderRightAction={(rule) => {
                        // Don't show menu for default rule or if disabled
                        if (!disabled && !rule.is_default) {
                            return (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >
                                                    <IconButton
                                                        onClick={(e) => handleMenuClick(e, rule.id)}
                                                        size="small"
                                                        sx={{
                                                            color: 'inherit',
                                                            padding: '8px',
                                                            minWidth: '40px',
                                                            minHeight: '40px',
                                                            '&:hover': {
                                                                backgroundColor: theme.colors.overlayHover,
                                                            },
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                                        </Box>
                                                    </IconButton>
                                    <Menu
                                        anchorEl={menuAnchorEl}
                                        open={Boolean(menuAnchorEl) && menuRuleId === rule.id}
                                        onClose={handleMenuClose}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                    >
                                        <MenuItem onClick={() => handleEditFromMenu(rule)}>Edit</MenuItem>
                                        <MenuItem 
                                            onClick={() => handleDeleteRule(rule.id)} 
                                            disabled={deletingRuleId === rule.id}
                                        >
                                            {deletingRuleId === rule.id ? 'Deleting...' : 'Delete'}
                                        </MenuItem>
                                    </Menu>
                                </Box>
                            );
                        }
                        return null;
                    }}
                    containerProps={{
                        component: Paper,
                        sx: {
                            mb: 0,
                            borderRadius: expandedView ? 0 : 2,
                            backgroundColor: expandedView ? 'transparent' : (theme.colors.tableBackground || theme.colors.background),
                            boxShadow: expandedView ? 'none' : (theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)'),
                            overflow: 'visible',
                            '& .MuiTable-root': {
                                '& .MuiTableBody-root': {
                                    '& .MuiTableRow-root:last-child': {
                                        '& .MuiTableCell-root': {
                                            borderBottom: 'none',
                                        },
                                    },
                                },
                            },
                        }
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, verticalAlign: 'middle' }}>
                                {headerAction ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                        {headerAction}
                                    </Box>
                                ) : null}
                            </TableCell>
                            <TableCell sx={{ color: theme.colors.text, fontWeight: 600, pl: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography sx={{ color: theme.colors.brandSecondary, fontWeight: 700, fontSize: '1.75rem' }}>Rules</Typography>
                                    {!disabled && (
                                        <BillRuleHistoryDropdown templateId={templateId} disabled={disabled} />
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell align="right" sx={{
                                color: theme.colors.text,
                                fontWeight: 600,
                                padding: { xs: '16px 2px', sm: '16px 0' },
                                width: { xs: '50px', sm: '100px' },
                                minWidth: { xs: '50px', sm: '100px' },
                                maxWidth: { xs: '50px', sm: '100px' },
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setTestModalOpen(true)}
                                        disabled={disabled}
                                        sx={{
                                            color: theme.colors.text,
                                            borderColor: theme.colors.border || theme.colors.text,
                                            '&:hover': {
                                                borderColor: theme.colors.primary || theme.colors.brandSecondary,
                                                color: theme.colors.primary || theme.colors.brandSecondary,
                                            },
                                        }}
                                    >
                                        Test
                                    </Button>
                                </Box>
                            </TableCell>
                            <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                            {allRulesForDisplay.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.colors.text,
                                                opacity: 0.7,
                                                fontStyle: 'italic',
                                                py: 2,
                                            }}
                                        >
                                            No rules configured yet
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                allRulesForDisplay.map((rule, index) => {
                                    const isDefault = rule.is_default;
                                    const isDraggable = !disabled && !isDefault; // Default rule cannot be moved
                                    const totalPercent = rule.rule_type === 'percent' && rule.participants
                                        ? rule.participants.reduce((sum, p) => sum + (p.percentage || 0), 0)
                                        : null;

                                    const isDragging = draggedRuleId === rule.id;
                                    const isDragOver = dragOverRuleId === rule.id;

                                    // Show drop indicator based on position (but not for default rule)
                                    const showDropIndicatorBefore = !isDefault && isDragOver && dragOverPosition === 'before' && draggedRuleId && draggedRuleId !== rule.id;
                                    const showDropIndicatorAfter = !isDefault && isDragOver && dragOverPosition === 'after' && draggedRuleId && draggedRuleId !== rule.id;

                                    return (
                                        <React.Fragment key={rule.id}>
                                            {/* Drop indicator line - appears before the row when inserting above */}
                                            {showDropIndicatorBefore && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={4}
                                                        sx={{
                                                            p: 0,
                                                            height: 0,
                                                            borderTop: `3px solid ${theme.colors.text}`,
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '-12px',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                backgroundColor: theme.colors.primary || '#1976d2',
                                                                color: 'white',
                                                                px: 1.5,
                                                                py: 0.5,
                                                                borderRadius: '4px',
                                                                fontSize: '10px',
                                                                fontWeight: 600,
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {/* Normal rule row - only show if NOT editing this rule */}
                                            {editingRule?.id !== rule.id && (
                                            <TableRow
                                                draggable={false}
                                                onDragOver={isDraggable ? (e) => handleDragOver(e, rule.id) : undefined}
                                                onDragLeave={isDraggable ? handleDragLeave : undefined}
                                                onDrop={isDraggable ? (e) => handleDrop(e, rule.id) : undefined}
                                                onDragEnd={isDraggable ? handleDragEnd : undefined}
                                                sx={{
                                                    cursor: 'default',
                                                    opacity: isDragging ? 0.5 : 1,
                                                    backgroundColor: isDragOver
                                                        ? `${theme.colors.primary || '#1976d2'}10`
                                                        : 'transparent',
                                                }}
                                            >
                                            {/* Left action column - rendered by TableWithActions via renderLeftAction */}
                                            <TableCell sx={{ color: theme.colors.text, width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}>
                                                {isDraggable ? (
                                                    <Box
                                                        draggable={isDraggable}
                                                        onDragStart={(e) => handleDragStart(e, rule.id)}
                                                        sx={{
                                                            cursor: isDraggable ? 'grab' : 'default',
                                                            '&:active': {
                                                                cursor: isDraggable ? 'grabbing' : 'default',
                                                            },
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            width: '100%',
                                                            height: '100%',
                                                        }}
                                                    >
                                                        <DotsSixVertical size={20} color={theme.colors.text} weight="bold" style={{ opacity: 0.5 }} />
                                                    </Box>
                                                ) : null}
                                            </TableCell>
                                            <TableCell sx={{ color: theme.colors.text, pl: 0, p: 0 }}>
                                                    {rule.label && (
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: theme.colors.text,
                                                                fontWeight: 600,
                                                                fontSize: '1.1rem',
                                                                pl: 0,
                                                                pb: 0.5,
                                                                display: 'block',
                                                            }}
                                                        >
                                                            {rule.label}
                                                        </Typography>
                                                    )}
                                                    {isDefault && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.colors.text,
                                                                opacity: 0.7,
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.5px',
                                                                pl: 0,
                                                                pb: 0.5,
                                                                display: 'block'
                                                            }}
                                                        >
                                                            default
                                                        </Typography>
                                                    )}
                                                    {rule.participants && rule.participants.length > 0 ? (
                                                        <Table size="small" sx={{ backgroundColor: 'transparent', width: '100%', margin: 0 }}>
                                                            <TableBody>
                                                                {rule.participants.map((participant, index) => {
                                                                    // Check if this is a vacancy (track exists but no user)
                                                                    let email = 'Unknown';
                                                                    if (participant.user?.email) {
                                                                        email = participant.user.email;
                                                                    } else if (participant.track && (!participant.track.user_id || participant.track.user_id === '')) {
                                                                        email = `Vacancy - Track ${participant.track.track_index || '?'}`;
                                                                    }
                                                                    const participantPct = participant.percentage;
                                                                    return (
                                                                        <TableRow key={participant.id || index} sx={{ border: 'none' }}>
                                                                            <TableCell
                                                                                sx={{
                                                                                    color: theme.colors.text,
                                                                                    py: 0.5,
                                                                                    pl: 0,
                                                                                    border: 'none',
                                                                                    fontSize: '1.1rem',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap',
                                                                                    maxWidth: 0, // Allows the cell to shrink
                                                                                }}
                                                                            >
                                                                                {email}
                                                                            </TableCell>
                                                                            <TableCell
                                                                                align="right"
                                                                                sx={{
                                                                                    color: theme.colors.text,
                                                                                    py: 0.5,
                                                                                    border: 'none',
                                                                                    fontSize: '1.1rem',
                                                                                    fontWeight: participantPct !== null && participantPct !== undefined ? 500 : 400,
                                                                                    width: '60px'
                                                                                }}
                                                                            >
                                                                                {participantPct !== null && participantPct !== undefined ? (
                                                                                    `${FormatNumber(participantPct)}%`
                                                                                ) : (
                                                                                    '-'
                                                                                )}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: theme.colors.text, opacity: 0.7, p: 1 }}>
                                                            None
                                                        </Typography>
                                                    )}
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                color: theme.colors.text,
                                                padding: { xs: '16px 2px', sm: '16px 0' },
                                                fontSize: '1.1rem',
                                                width: { xs: '50px', sm: '100px' },
                                                minWidth: { xs: '50px', sm: '100px' },
                                                maxWidth: { xs: '50px', sm: '100px' },
                                            }}>
                                                {rule.rule_type === 'minus' && rule.amount && `$${FormatNumber(rule.amount)}`}
                                                {rule.rule_type === 'percent' && (
                                                    <Typography sx={{
                                                        color: theme.colors.text,
                                                        fontWeight: 500,
                                                        fontSize: '1.1rem',
                                                    }}>
                                                        {rule.rule_percentage ? (
                                                            `${FormatNumber(rule.rule_percentage)}%`
                                                        ) : (
                                                            totalPercent !== null ? `${FormatNumber(totalPercent)}%` : '-'
                                                        )}
                                                    </Typography>
                                                )}
                                                {rule.rule_type === 'evenly' && (isDefault ? (
                                                    <Tooltip title="splitting remainder" arrow>
                                                        <Typography sx={{
                                                            color: theme.colors.text,
                                                            fontSize: '1rem',
                                                            fontWeight: 700,
                                                            cursor: 'help',
                                                        }}>
                                                            remainder
                                                        </Typography>
                                                    </Tooltip>
                                                ) : '-')}
                                            </TableCell>
                                            {/* Right action column - rendered by TableWithActions via renderRightAction */}
                                            <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}>
                                                {!disabled && !isDefault && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            width: '100%',
                                                            height: '100%',
                                                        }}
                                                    >
                                                    <IconButton
                                                        onClick={(e) => handleMenuClick(e, rule.id)}
                                                        size="small"
                                                        sx={{
                                                            color: 'inherit',
                                                            padding: '8px',
                                                            minWidth: '40px',
                                                            minHeight: '40px',
                                                            '&:hover': {
                                                                backgroundColor: theme.colors.overlayHover,
                                                            },
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                                        </Box>
                                                    </IconButton>
                                                        <Menu
                                                            anchorEl={menuAnchorEl}
                                                            open={Boolean(menuAnchorEl) && menuRuleId === rule.id}
                                                            onClose={handleMenuClose}
                                                            anchorOrigin={{
                                                                vertical: 'bottom',
                                                                horizontal: 'right',
                                                            }}
                                                            transformOrigin={{
                                                                vertical: 'top',
                                                                horizontal: 'right',
                                                            }}
                                                        >
                                                            <MenuItem onClick={() => handleEditFromMenu(rule)}>Edit</MenuItem>
                                                            <MenuItem 
                                                                onClick={() => handleDeleteRule(rule.id)} 
                                                                disabled={deletingRuleId === rule.id}
                                                            >
                                                                {deletingRuleId === rule.id ? 'Deleting...' : 'Delete'}
                                                            </MenuItem>
                                                        </Menu>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                            )}

                                            {/* Drop indicator line - appears after the row when inserting below */}
                                            {showDropIndicatorAfter && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={4}
                                                        sx={{
                                                            p: 0,
                                                            height: 0,
                                                            borderTop: `3px solid ${theme.colors.text}`,
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '-12px',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                backgroundColor: theme.colors.primary || '#1976d2',
                                                                color: 'white',
                                                                px: 1.5,
                                                                py: 0.5,
                                                                borderRadius: '4px',
                                                                fontSize: '10px',
                                                                fontWeight: 600,
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {/* Edit Rule Form - Inline (shows when this rule is being edited) */}
                                            {editingRule?.id === rule.id && (
                                                <EditRuleForm
                                                    templateId={templateId}
                                                    rule={editingRule}
                                                    isEditing={true}
                                                    onCancel={() => setEditingRule(null)}
                                                    onSuccess={() => {
                                                        queryClient.invalidateQueries(['billRules', String(templateId)]);
                                                        queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);
                                                        setEditingRule(null);
                                                    }}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}

                            {/* Create Rule Form - Inline */}
                            {!editingRule && (
                                <CreateRuleForm
                                    templateId={templateId}
                                    isCreating={isCreatingRule}
                                    setIsCreating={setIsCreatingRule}
                                    disabled={disabled}
                                    onSuccess={() => {
                                        queryClient.invalidateQueries(['billRules', String(templateId)]);
                                        queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);
                                        setIsCreatingRule(false);
                                    }}
                                />
                            )}
                        </TableBody>
                </TableWithActions>
                </>
            )}
            </>
            )}

            {/* Edit Percent Rule Percentage Dialog */}
            <EditPercentRulePercentageForm
                templateId={templateId}
                rule={editingPercentRulePercentage}
                open={!!editingPercentRulePercentage}
                onClose={() => setEditingPercentRulePercentage(null)}
                onSuccess={() => {
                    queryClient.invalidateQueries(['billRules', String(templateId)]);
                    queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);
                }}
            />

            {/* Test splits modal - shows participants and estimated splits (fetched on open) */}
            <TestSplitsModal
                open={testModalOpen}
                onClose={() => setTestModalOpen(false)}
                templateId={templateId}
            />
        </Box>
    );
};

export default RulesList;
